import React from 'react';
import { Trash2, Archive, Send, Users, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import DataTable from './ui/DataTable';

const QueryResultsTable = ({ 
  data = [], 
  error = null, 
  onRowAction = () => {},
  selectedStatus = 1,
  updating = false,
  crawlingResultIds = [],
  scanQueue = []
}) => {
  const { t } = useTranslation();

  // Helper function to count contacts
  const getContactCount = (row) => {
    if (!row.contacts) return 0;
    
    try {
      const contactsData = typeof row.contacts === 'string' 
        ? JSON.parse(row.contacts) 
        : row.contacts;
      
      const emailCount = contactsData?.emails?.length || 0;
      const phoneCount = contactsData?.phones?.length || 0;
      const socialCount = contactsData?.socials ? Object.keys(contactsData.socials).length : 0;
      
      return emailCount + phoneCount + socialCount;
    } catch (err) {
      return 0;
    }
  };

  // Generate columns with business logic
  const getColumns = () => [
    {
      key: 'url',
      header: t('url'),
      render: (url, row) => (
        <a 
          href={url} 
          target="_blank" 
          rel="noopener noreferrer" 
          className="text-muted-foreground hover:text-foreground break-all"
          title={url}
        >
          {url}
        </a>
      )
    },
    {
      key: 'title',
      header: t('title'),
      fontWeight: 'font-medium'
    },
    {
      actions: [
        {
          key: 'delete',
          icon: <Trash2 className="h-4 w-4" />,
          label: '',
          title: selectedStatus === 2 ? t('undelete') : t('delete'),
          variant: 'ghost',
          size: 'sm',
          className: 'text-muted-foreground hover:text-foreground',
          disabled: updating
        },
        // Show archive/unarchive button only for active and archived statuses
        ...(selectedStatus !== 2 ? [{
          key: 'archive',
          icon: <Archive className="h-4 w-4" />,
          label: '',
          title: selectedStatus === 0 ? t('unarchive') : t('archive'),
          variant: 'ghost',
          size: 'sm',
          className: 'text-muted-foreground hover:text-foreground',
          disabled: updating
        }] : []),
        // Contacts button - will be conditionally displayed by action filters
        {
          key: 'contacts',
          icon: <Users className="h-4 w-4" />,
          label: (row) => {
            const count = getContactCount(row);
            return count > 0 ? `(${count})` : '';
          },
          title: t('contacts'),
          variant: 'ghost',
          size: 'sm',
          className: 'text-muted-foreground hover:text-foreground',
          disabled: updating
        },
        // Check Website button - styled like Contact but in yellow
        {
          key: 'checkWebsite',
          icon: <Search className="h-4 w-4" />,
          label: 'Scan',
          title: t('checkWebsite'),
          variant: 'outline',
          size: 'sm',
          className: 'bg-yellow-50 border-yellow-300 text-yellow-600 hover:bg-yellow-100 hover:border-yellow-400 hover:text-yellow-700 transition-colors',
          disabled: updating
        },
        // Submit button - will be conditionally displayed by action filters
        {
          key: 'submit',
          icon: <Send className="h-4 w-4" />,
          label: t('contact'),
          title: t('contact'),
          variant: 'outline',
          size: 'sm',
          className: 'bg-green-50 border-green-300 text-green-600 hover:bg-green-100 hover:border-green-400 hover:text-green-700 transition-colors',
          disabled: updating
        }
      ]
    }
  ];

  return (
    <DataTable
      data={data}
      columns={getColumns()}
      error={error}
      emptyMessage={t('noResults')}
      onRowAction={onRowAction}
      cardView={true}
      cardTitle={(row) => row.title}
      cardSubtitle={(row) => (
        <a 
          href={row.url} 
          target="_blank" 
          rel="noopener noreferrer" 
          className="text-muted-foreground hover:text-foreground break-all"
        >
          {row.url}
        </a>
      )}
      getSocials={(row) => {
        // Parse contacts JSON to extract social media links
        if (!row.contacts) return null;
        try {
          const contactsData = typeof row.contacts === 'string' 
            ? JSON.parse(row.contacts) 
            : row.contacts;
          return contactsData?.socials || null;
        } catch (err) {
          console.error('Error parsing contacts for socials:', err);
          return null;
        }
      }}
      getContacts={(row) => {
        // Parse contacts JSON to extract email and phone data
        if (!row.contacts) return null;
        try {
          const contactsData = typeof row.contacts === 'string' 
            ? JSON.parse(row.contacts) 
            : row.contacts;
          return {
            emails: contactsData?.emails || [],
            phones: contactsData?.phones || []
          };
        } catch (err) {
          console.error('Error parsing contacts for email/phone:', err);
          return null;
        }
      }}
      actionState={(row, action) => {
        if (action.key === 'checkWebsite') {
          // Check if this row is being scanned (manual or auto-scan)
          if (crawlingResultIds.includes(row.id) || scanQueue.includes(row.id)) {
            return { loading: true };
          }
        }
        return {};
      }}
    />
  );
};

export default QueryResultsTable;
